import "./App.css";
import {RouterProvider} from "react-router";
import {useAuth} from "react-oidc-context";
import {useEffect, useRef, useState} from "react";
import {type RootState, store} from "./store";
import authService from "./service/authService.ts";
import {useRouter} from "./service/routeProvider.tsx";
import {useSupportContextHistory} from "./hooks/useSupportContextHistory.ts";
import PrivateRoute from "./pages/loginPage/PrivateRoute.tsx";
import {type CockpitUser} from "./model/user/CockpitUser.ts";
import {useSelector} from "react-redux";
import GlobalLoadPage from "./components/layout/GlobalLoadPage.tsx";
import {useTranslation} from "react-i18next";
import {ErrorBoundary} from "react-error-boundary";
import ErrorFallback from "./components/error/ErrorFallback";

const App = () => {
    const {t} = useTranslation();
    const auth = useAuth();
    const {router, key: routerKey} = useRouter();
    useSupportContextHistory(router);
    const profile = useSelector((state: RootState) => state.auth.profile);
    const [isInitialized, setIsInitialized] = useState(false);

    const dispatchUserToRedux = (user: CockpitUser) => {
        if (user) {
            store.dispatch(authService.loginSuccess(
                user.profile,
                user.access_token,
                user.profile["user-scope"]
            ));
        }
    };

    // Run-once guard: signinSilent() itself toggles auth.isLoading, so this
    // effect re-fires while the call is in flight — without the guard that
    // spirals into an endless token-refresh loop.
    const initAttempted = useRef(false);
    useEffect(() => {
        // Wait until the AuthProvider has loaded the stored user (if any).
        if (auth.isLoading || initAttempted.current) return;
        initAttempted.current = true;
        // Only attempt the silent signin when a stored user exists — it then runs
        // over the refresh token. Without one, oidc-client-ts falls back to the
        // hidden-iframe flow, which the login page blocks via X-Frame-Options, so
        // the attempt only fails after a long timeout while the user stares at
        // the splash screen (#55620).
        if (!auth.user) {
            setIsInitialized(true);
            return;
        }
        void auth.signinSilent()
            .then(user => {
                dispatchUserToRedux(user as CockpitUser);
            })
            .catch(() => {
                // Not signed in anymore — the login page takes over.
            })
            .finally(() => {
                setIsInitialized(true);
            });
        // auth from useAuth() is a stable reference for our purposes here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.isLoading]);

    useEffect(() => {
        return auth.events.addAccessTokenExpiring(() => {
            void auth.signinSilent().then(user => {
                dispatchUserToRedux(user as CockpitUser);
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (auth.isAuthenticated && !profile) {
            dispatchUserToRedux(auth.user as CockpitUser);
        }
    }, [auth?.user, auth.isAuthenticated, profile]);

    if (!isInitialized) {
        return <GlobalLoadPage/>;
    }

    return (
        <ErrorBoundary fallbackRender={({error}) => <ErrorFallback error={error as Error} showBaseNotice/>}>
            <title>{t("defaults.title")}</title>
            <PrivateRoute component={<RouterProvider key={routerKey} router={router}/>}/>
        </ErrorBoundary>);
};

export default App;
